import type { ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react'

interface HeaderProps {
  title: string
  value: string | number
  Icon: React.ElementType
  isExpanded: boolean
  onToggle: () => void
}

export function TileHeader({
  title,
  value,
  Icon,
  isExpanded,
  onToggle,
}: HeaderProps): ReactElement {
  return (
    <button onClick={onToggle} className="w-full p-6 text-left" aria-expanded={isExpanded}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
            {title}
          </p>
          <p className="mt-2 text-3xl font-semibold" style={{ color: 'var(--text)' }}>
            {value}
          </p>
        </div>
        <div className="flex items-start gap-2">
          <div className="rounded-lg p-3" style={{ background: 'var(--accent-dim)' }}>
            <Icon className="h-6 w-6" style={{ color: 'var(--accent)' }} />
          </div>
          <div
            className="cursor-pointer rounded-lg p-2 transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </div>
    </button>
  )
}

interface ExpandedProps {
  isExpanded: boolean
  loading: boolean
  children?: ReactNode
  viewAllLink: string | undefined
  viewAllLabel: string
}

export function ExpandedContent({
  isExpanded,
  loading,
  children,
  viewAllLink,
  viewAllLabel,
}: ExpandedProps): ReactElement {
  return (
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="border-t" style={{ borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="db-spinner h-6 w-6 animate-spin rounded-full border-2" />
          </div>
        ) : (
          <div className="p-6 pt-4">
            {children}
            {viewAllLink && (
              <Link
                to={viewAllLink}
                className="mt-4 flex items-center gap-1 text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: 'var(--accent)' }}
              >
                {viewAllLabel} <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
